import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import EditIcon from "../../../../src/assets/icons/EditIcon.svg";
import DeleteIcon from "../../../../src/assets/icons/DeleteIcon.svg";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";

// --- Reusable Components ---

const FormSection = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <Paper className="mb-4 shadow-sm border-0">
    <Box className="px-3 pt-4 pb-5">
      {title && (
        <Typography
          variant="h6"
          className="fw-bold mb-3"
          sx={{ color: "rgba(13,44,90,1)" }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Box>
  </Paper>
);

const TextInput = ({
  field,
  formData,
  onChange,
  placeholder,
  type = "text",
  label,
  required = false,
  endAdornment,
  error,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  placeholder?: string;
  type?: string;
  label?: string;
  required?: boolean;
  endAdornment?: React.ReactNode;
  error?: string;
}) => (
  <TextField
    fullWidth
    size="small"
    type={type}
    variant="outlined"
    label={label}
    required={required}
    value={formData[field]}
    placeholder={placeholder}
    onChange={(e) => onChange(field, e.target.value)}
    error={Boolean(error)}
    helperText={error}
    InputProps={{
      endAdornment: endAdornment,
    }}
    sx={{
      "& .MuiFormLabel-asterisk": {
        color: "red",
      },
    }}
  />
);


// --- Main Component ---

// Template Data Interface
interface TemplateData {
  id: string;
  templateName: string;
  extraChargePerKm: number;
  lateReturnFee: number;
  freeHours: number;
  remarks: string;
  latereturntype?: number;
}

// API Template Data Interface
interface ApiTemplateData {
  ratetemplatemasterid: number;
  templatename: string;
  remarks: string;
  templatestatus: number;
  createdby: number;
  createdon: string;
  modifiedby: number;
  modifiedon: string;
  latereturntype?: number;
  ratetemplatechargemodel?: any[];
}

const CreatePlanPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const {
    options: lateReturnTypeOptions,
    loading: lateReturnTypeLoading,
    error: lateReturnTypeError,
    refetch: refetchLateReturnType,
  } = useCodeCommon("LateReturnType", true);

  const [formData, setFormData] = useState({
    templateName: "",
    extraChargePerKm: "",
    lateReturnFeePerHours: "",
    freeHours: "",
    remarks: "",
    lateFeeType: "", // Will store codedisplayname from API
  });

  // Get current user ID (you may need to get this from context or auth)
  const currentUserId = 40; // This should come from your auth context
  const currentDate = new Date().toISOString();

  // Template data for the table
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalTemplateName, setOriginalTemplateName] = useState<string>("");

  const getLateReturnTypeId = (lateReturnTypeName: string): number => {
    const lateReturnTypeOption = lateReturnTypeOptions.find(
      (option) => option.codedisplayname === lateReturnTypeName
    );
    return lateReturnTypeOption ? parseInt(lateReturnTypeOption.codevalue) : 1;
  };

  const isHourType = (lateReturnTypeName: string): boolean => {
    const lateReturnTypeOption = lateReturnTypeOptions.find(
      (option) => option.codedisplayname === lateReturnTypeName
    );
    // codevalue 1 = Hours, codevalue 2 = Days
    return lateReturnTypeOption ? parseInt(lateReturnTypeOption.codevalue) === 1 : false;
  };

  const getLateReturnTypeDisplayName = (latereturntype: number): string => {
    if (lateReturnTypeOptions && lateReturnTypeOptions.length > 0) {
      const lateReturnTypeOption = lateReturnTypeOptions.find(
        (option) => parseInt(option.codevalue) === latereturntype
      );
      return lateReturnTypeOption ? lateReturnTypeOption.codedisplayname : 'Hours';
    }
    // Default fallback: codevalue 1 = Hours, codevalue 2 = Days
    return latereturntype === 2 ? 'Days' : 'Hours';
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Real-time validation for template name (only when creating, not editing)
    if (field === 'templateName' && !isEditing) {
      const trimmedValue = String(value ?? '').trim();
      if (trimmedValue !== '') {
        const existingTemplate = templates.find(
          (template) => 
            template.templateName.toLowerCase() === trimmedValue.toLowerCase()
        );
        if (existingTemplate) {
          setErrors((prev) => ({
            ...prev,
            templateName: 'Template name already exists. Please choose a different name.'
          }));
        } else {
          // Clear error if name is unique
          setErrors((prev) => {
            const next = { ...prev };
            delete next.templateName;
            return next;
          });
        }
      } else {
        // Clear error if field is empty
        setErrors((prev) => {
          const next = { ...prev };
          delete next.templateName;
          return next;
        });
      }
    } else {
      // Clear error for other fields on valid input
      setErrors((prev) => {
        const next = { ...prev };
        if (String(value ?? '').trim() !== '') delete (next as any)[field];
        return next;
      });
    }
  };

  // Function to transform API template data to display format
  const transformApiTemplateToDisplay = (apiTemplate: ApiTemplateData): TemplateData => {
    // Extract data from ratetemplatechargemodel
    let extraChargePerKm = 0;
    let lateReturnFee = 0;
    let freeHours = 0;
    
    if (apiTemplate.ratetemplatechargemodel && apiTemplate.ratetemplatechargemodel.length > 0) {
      // Find extra KM charge (chargetypeid: 2)
      const extraKmCharge = apiTemplate.ratetemplatechargemodel.find(
        (charge: any) => charge.chargetypeid === 2
      );
      if (extraKmCharge && extraKmCharge.ratetemplatechargedetails && extraKmCharge.ratetemplatechargedetails.length > 0) {
        // Get unitamount from details (chargeamount is null in API response)
        extraChargePerKm = extraKmCharge.ratetemplatechargedetails[0].unitamount || 0;
      }

      // Find late return charge (chargetypeid: 3)
      const lateReturnCharge = apiTemplate.ratetemplatechargemodel.find(
        (charge: any) => charge.chargetypeid === 3
      );
      if (lateReturnCharge && lateReturnCharge.ratetemplatechargedetails && lateReturnCharge.ratetemplatechargedetails.length > 0) {
        // Get unitamount from details for late return fee (chargeamount is null in API response)
        lateReturnFee = lateReturnCharge.ratetemplatechargedetails[0].unitamount || 0;
        // Get startunit for free hours
        freeHours = lateReturnCharge.ratetemplatechargedetails[0].startunit || 0;
      }
    }

    // Get latereturntype from late return charge if it exists
    let latereturntype = 0;
    // First check root level latereturntype
    if (apiTemplate.latereturntype !== undefined && apiTemplate.latereturntype !== null) {
      latereturntype = apiTemplate.latereturntype;
    }
    // Then check in late return charge (chargetypeid: 3)
    if (apiTemplate.ratetemplatechargemodel && apiTemplate.ratetemplatechargemodel.length > 0) {
      const lateReturnCharge = apiTemplate.ratetemplatechargemodel.find(
        (charge: any) => charge.chargetypeid === 3
      );
      if (lateReturnCharge) {
        // Override with latereturntype from charge if available
        if (lateReturnCharge.latereturntype !== undefined && lateReturnCharge.latereturntype !== null) {
          latereturntype = lateReturnCharge.latereturntype;
        }
      }
    }

    return {
      id: apiTemplate.ratetemplatemasterid.toString(),
      templateName: apiTemplate.templatename,
      extraChargePerKm: extraChargePerKm,
      lateReturnFee: lateReturnFee,
      freeHours: freeHours,
      remarks: apiTemplate.remarks,
      latereturntype: latereturntype,
    };
  };

  // Function to fetch templates from API
  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);

    try {
      const response = await apiService.getAllTemplates();
      
      // Handle different possible response structures
      let templatesData: ApiTemplateData[] = [];
      if (Array.isArray(response)) {
        templatesData = response;
      } else if (response && (response as any).data) {
        templatesData = (response as any).data;
      } else if (response && (response as any).result) {
        templatesData = (response as any).result;
      } else if (response && (response as any).templates) {
        templatesData = (response as any).templates;
      }

      // Transform API data to display format
      const transformedTemplates = templatesData.map(transformApiTemplateToDisplay);
      setTemplates(transformedTemplates);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setTemplatesError(err.message || 'Failed to load templates. Please try again.');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Validate required fields
      const nextErrors: Record<string, string> = {};
      const requireField = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        }
      };

      const requirePositiveNumber = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        } else {
          const numValue = parseFloat(String(value));
          if (isNaN(numValue) || numValue < 0) {
            nextErrors[key] = `${label} must be a positive number`;
          }
        }
      };

      // All fields are required
      requireField('templateName', 'Template Name');
      requirePositiveNumber('extraChargePerKm', 'Extra Charge Per KM');
      requireField('lateFeeType', 'Late Fee');
      if (formData.lateFeeType) {
        requirePositiveNumber('lateReturnFeePerHours', `Late Return Fee Per ${formData.lateFeeType}`);
        requirePositiveNumber('freeHours', `Free ${formData.lateFeeType === "Days" ? "Days" : "Hours"}`);
      }
      requireField('remarks', 'Remarks');

      // Check if template name already exists (only when creating, not editing)
      if (!isEditing && formData.templateName && formData.templateName.trim()) {
        const trimmedTemplateName = formData.templateName.trim();
        const existingTemplate = templates.find(
          (template) => 
            template.templateName.toLowerCase() === trimmedTemplateName.toLowerCase()
        );
        if (existingTemplate) {
          nextErrors.templateName = 'Template name already exists. Please choose a different name.';
        }
      }

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        throw new Error('Please fill all required fields');
      }

      // Create API payload according to the specified structure
      const apiPayload = {
        ratetemplatemasterid: isEditing ? parseInt(editingTemplateId || '0') : 0,
        templatename: formData.templateName,
        remarks: formData.remarks,
        templatestatus: 1,
        latereturntype: getLateReturnTypeId(formData.lateFeeType),
        createdby: currentUserId,
        createdon: currentDate,
        modifiedby: currentUserId,
        modifiedon: currentDate,
        ratetemplatechargemodel: [
          {
            // base rate
            ratetemplatechargeid: 0, // static
            ratetemplatemasterid: 0, // static
            chargetypeid: 1, // getAllChargeTypeList
            chargeamount: 0, // static
            chargemethodid: 1, // GetAllChargeMethodsList
            createdby: currentUserId,
            createdon: currentDate,
            modifiedby: 0,
            modifiedon: currentDate,
            latereturntype: 0,
            ratetemplatechargedetails: [
              {
                ratetemplatechargedetailid: 0,
                ratetemplatechargeid: 0,
                startunit: 1,
                endunit: 400,
                startchargeamount: 0,
                minchargeamount: 0,
                unitincrement: 1,
                unitamount: 0,
                createdby: currentUserId,
                createdon: currentDate,
                modifiedby: 0,
                modifiedon: currentDate
              }
            ]
          },
          {
            // extra KM charge
            ratetemplatechargeid: 0,
            ratetemplatemasterid: 0,
            chargetypeid: 2,
            chargeamount: parseFloat(formData.extraChargePerKm) || 0,
            chargemethodid: 2,
            createdby: currentUserId,
            createdon: currentDate,
            modifiedby: 0,
            modifiedon: currentDate,
            latereturntype: 0,
            ratetemplatechargedetails: [
              {
                ratetemplatechargedetailid: 0,
                ratetemplatechargeid: 0,
                startunit: 1,
                endunit: 99999,
                startchargeamount: 0,
                minchargeamount: 0,
                unitincrement: 1,
                unitamount: 0,
                createdby: currentUserId,
                createdon: currentDate,
                modifiedby: 0,
                modifiedon: currentDate
              }
            ]
          },
          {
            // late return charge
            ratetemplatechargeid: 0,
            ratetemplatemasterid: 0,
            chargetypeid: 3,
            chargeamount: parseFloat(formData.lateReturnFeePerHours),
            chargemethodid: 2,
            createdby: currentUserId,
            createdon: currentDate,
            modifiedby: 0,
            modifiedon: currentDate,
            latereturntype: getLateReturnTypeId(formData.lateFeeType),
            ratetemplatechargedetails: [
              {
                ratetemplatechargedetailid: 0,
                ratetemplatechargeid: 0,
                startunit: parseFloat(formData.freeHours), // Hours
                endunit: 0, // static
                startchargeamount: 0, // static
                minchargeamount: 0, // static
                unitincrement: 1, // static
                 unitamount: 0,
                createdby: currentUserId,
                createdon: currentDate,
                modifiedby: 0,
                modifiedon: currentDate
              }
            ]
          }
        ]
      };

      // Call appropriate API based on whether we're creating or editing
      if (isEditing) {
        await apiService.updateTemplate(apiPayload);
      } else {
        await apiService.createTemplate(apiPayload);
      }

      // Refresh templates list from API
      await fetchTemplates();

      // Reset form and editing state
      setFormData({
        templateName: "",
        extraChargePerKm: "",
        lateReturnFeePerHours: "",
        freeHours: "",
        remarks: "",
        lateFeeType: "",
      });
      setEditingTemplateId(null);
      setIsEditing(false);
      setOriginalTemplateName("");
      setErrors({});

      setSuccess(isEditing ? "Template updated successfully!" : "Template created successfully!");
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error creating template:', err);
      setError(err.message || "Failed to create template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Call API to get template details
      const response = await apiService.getTemplateByIdOrName(templateId);
      
      // Handle different possible response structures
      let templateData: ApiTemplateData;
      if (Array.isArray(response)) {
        templateData = response[0];
      } else if (response && (response as any).data) {
        templateData = (response as any).data;
      } else if (response && (response as any).result) {
        templateData = (response as any).result;
      } else {
        templateData = response as ApiTemplateData;
      }

      // Transform API data to display format
      const transformedTemplate = transformApiTemplateToDisplay(templateData);
      
      // Get late fee type display name based on latereturntype
      const lateFeeTypeDisplayName = transformedTemplate.latereturntype 
        ? getLateReturnTypeDisplayName(transformedTemplate.latereturntype)
        : "";
      
      // Populate form with template data
      setFormData({
        templateName: transformedTemplate.templateName,
        extraChargePerKm: transformedTemplate.extraChargePerKm.toString(),
        lateReturnFeePerHours: transformedTemplate.lateReturnFee.toString(),
        freeHours: transformedTemplate.freeHours.toString(),
        remarks: transformedTemplate.remarks,
        lateFeeType: lateFeeTypeDisplayName, // Set default based on latereturntype
      });

      // Set editing state
      setEditingTemplateId(templateId);
      setIsEditing(true);
      setOriginalTemplateName(transformedTemplate.templateName);
      setErrors({});
    } catch (err: any) {
      console.error('Error fetching template for edit:', err);
      setError(err.message || "Failed to load template for editing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle delete template - open confirmation dialog
  const handleDeleteTemplate = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  // Function to confirm delete template
  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeleting(true);
      setError(null);
      
      // Call API to delete template
      await apiService.deleteTemplate(templateToDelete);
      
      // Close dialog and refresh templates list from API
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      await fetchTemplates();
      
      setSuccess("Template deleted successfully!");
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error deleting template:', err);
      setError(err.message || "Failed to delete template. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Function to cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  return (
    <Container fluid>
      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" className="mb-3" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" className="mb-3" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <FormSection title="Template Details">
        {/* Template Form Fields */}
        <Row className="g-3 mb-3">
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="templateName"
              formData={formData}
              onChange={handleInputChange}
              label="Template Name"
              placeholder="Enter template name"
              required
              error={errors.templateName}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="extraChargePerKm"
              formData={formData}
              onChange={handleInputChange}
              label="Extra Charge(/km)"
              type="number"
              placeholder="Enter extra charge per km"
              required
              error={errors.extraChargePerKm}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.lateFeeType)}>
              <InputLabel>Late Fee</InputLabel>
              <Select
                value={formData.lateFeeType}
                onChange={(e) => handleInputChange("lateFeeType", e.target.value)}
                label="Late Fee"
                sx={{
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                }}
              >
                {lateReturnTypeLoading ? (
                  <MenuItem value="" disabled>
                    Loading...
                  </MenuItem>
                ) : lateReturnTypeOptions && lateReturnTypeOptions.length > 0 ? (
                  lateReturnTypeOptions.map((option) => (
                    <MenuItem key={option.codevalue} value={option.codedisplayname}>
                      {option.codedisplayname}
                    </MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value="Day">Day</MenuItem>
                    <MenuItem value="Hour">Hour</MenuItem>
                  </>
                )}
              </Select>
              {errors.lateFeeType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.lateFeeType}
                </Typography>
              )}
            </FormControl>
          </Col>
          {formData.lateFeeType && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="lateReturnFeePerHours"
                formData={formData}
                onChange={handleInputChange}
                label={`Late Return Fee(/${formData.lateFeeType === "Days" ? "Day" : "Hour"})`}
                type="number"
                placeholder={`Enter late return fee per ${formData.lateFeeType === "Days" ? "day" : "hour"}`}
                required
                error={errors.lateReturnFeePerHours}
              />
            </Col>
          )}
          {formData.lateFeeType && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="freeHours"
                formData={formData}
                onChange={handleInputChange}
                label={`Free ${formData.lateFeeType === "Days" ? "Days" : "Hours"}`}
                type="number"
                placeholder={`Enter free ${formData.lateFeeType === "Days" ? "days" : "hours"}`}
                required
                error={errors.freeHours}
              />
            </Col>
          )}
        </Row>

        {/* Remarks Field - Full Width */}
        <Row className="g-3 mb-3">
          <Col xs={6}>
            <TextInput
              field="remarks"
              formData={formData}
              onChange={handleInputChange}
              label="Remarks"
              placeholder="Enter remarks"
              required
              error={errors.remarks}
            />
          </Col>
        </Row>
      </FormSection>

      {/* Action Buttons */}
      <Box className="d-flex justify-content-end gap-3 mt-4 mb-4">
        <Button
          variant="contained"
          className="px-4 py-2 fw-medium text-white"
          disabled={loading}
          onClick={handleSave}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "100px",
            "&:disabled": { backgroundColor: "rgba(13,44,90,0.5)" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" /> {isEditing ? "Updating..." : "Saving..."}
            </Box>
          ) : (isEditing ? "Update" : "Save")}
        </Button>
        {isEditing ? (
          <Button
            variant="outlined"
            className="px-4 py-2 fw-medium"
            onClick={() => {
              setFormData({
                templateName: "",
                extraChargePerKm: "",
                lateReturnFeePerHours: "",
                freeHours: "",
                remarks: "",
                lateFeeType: "",
              });
              setEditingTemplateId(null);
              setIsEditing(false);
              setOriginalTemplateName("");
              setError(null);
              setSuccess(null);
              setErrors({});
            }}
            sx={{
              borderColor: "rgba(13,44,90,1)",
              color: "rgba(13,44,90,1)",
              "&:hover": {
                borderColor: "rgba(13,44,90,1)",
                backgroundColor: "rgba(13,44,90,0.04)",
              },
            }}
          >
            Cancel Edit
          </Button>
        ) : (
          <Button
            variant="outlined"
            className="px-4 py-2 fw-medium"
            onClick={onBack}
            sx={{
              borderColor: "rgba(13,44,90,1)",
              color: "rgba(13,44,90,1)",
              "&:hover": {
                borderColor: "rgba(13,44,90,1)",
                backgroundColor: "rgba(13,44,90,0.04)",
              },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>

      {/* Template List Section */}
      <FormSection title="Template List">
        {/* Error Display for Templates */}
        {templatesError && (  
          <Alert severity="error" className="mb-3" onClose={() => setTemplatesError(null)}>
            {templatesError}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
               <TableRow sx={{ backgroundColor: 'rgba(240, 248, 255, 1)' }}>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', width: '16%', textAlign: 'center' }}>Template Name</TableCell>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', width: '15%', textAlign: 'center' }}>
                   Extra Charge <br/>
                   <span style={{ textAlign: 'center' }}>(Per KM)</span>
                 </TableCell>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', width: '16%', textAlign: 'center' }}>
                   Late Return Fee
                   <br />
                   <span style={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)' }}>(Day or Hour)</span>
                 </TableCell>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', width: '16%', textAlign: 'center' }}>
                   Grace Period
                   <br />
                   <span style={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)' }}>(Day or Hour)</span>
                 </TableCell>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', width: '18%', textAlign: 'center' }}>Remarks</TableCell>
                 <TableCell sx={{ fontWeight: 'bold', color: 'rgba(13,44,90,1)', textAlign: 'center', width: '14%' }}>Action</TableCell>
               </TableRow>
            </TableHead>
            <TableBody>
              {templatesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading templates...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No templates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell sx={{ width: '16%', textAlign: 'center' }}>{template.templateName}</TableCell>
                    <TableCell sx={{ width: '15%', textAlign: 'center' }}>₹{template.extraChargePerKm}</TableCell>
                    <TableCell sx={{ width: '16%', textAlign: 'center' }}>
                      {template.lateReturnFee > 0 
                        ? `₹${template.lateReturnFee} / ${getLateReturnTypeDisplayName(template.latereturntype || 0)}`
                        : `₹0 / ${getLateReturnTypeDisplayName(template.latereturntype || 0)}`
                      }
                    </TableCell>
                    <TableCell sx={{ width: '16%', textAlign: 'center' }}>
                      {template.freeHours > 0 
                        ? `${template.freeHours} ${getLateReturnTypeDisplayName(template.latereturntype || 0)}`
                        : `0 ${getLateReturnTypeDisplayName(template.latereturntype || 0)}`
                      }
                    </TableCell>
                    <TableCell sx={{ width: '18%', textAlign: 'center', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {template.remarks}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', width: '14%' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditTemplate(template.id)}
                          sx={{ p: 0.5 }}
                        >
                          <img src={EditIcon} alt="Edit" width={30} height={30} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTemplate(template.id)}
                          sx={{ p: 0.5 }}
                        >
                          <img src={DeleteIcon} alt="Delete" width={30} height={30} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormSection>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDelete} 
            disabled={deleting}
            sx={{ color: "rgba(13, 44, 90, 1)" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteTemplate} 
            disabled={deleting}
            variant="contained"
            sx={{ 
              backgroundColor: "rgba(197, 0, 0, 1)",
              "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" }
            }}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreatePlanPage;


